<script setup lang="ts">
import { computed } from 'vue';
import * as Locale from '@/plugins/i18n';
import EndCallIcon from '@/components/icons/EndCallIcon.vue';
import MicIcon from '@/components/icons/MicIcon.vue';
import MicOffIcon from '@/components/icons/MicOffIcon.vue';
import { useVoiceCallIndicatorCustomisation } from './useVoiceCallIndicatorCustomisation';
import * as SUtils from '@/common/customisation/utils/styleUtils';

export interface VoiceCallIndicatorProps {
  callDuration?: string;
  disabled?: boolean;
  showMuteButton?: boolean;
  isMuted?: boolean;
}

const props = withDefaults(defineProps<VoiceCallIndicatorProps>(), {
  callDuration: '00:00',
  disabled: false,
  showMuteButton: false,
  isMuted: false,
});

const emit = defineEmits<{
  (e: 'end-call'): void;
  (e: 'toggle-mute'): void;
}>();

const customisation = useVoiceCallIndicatorCustomisation();

const text = computed(() => {
  return customisation.textContent.value || (Locale.i18n.t('chat.voice_call.in_progress') as string);
});

const containerStyle = computed(() => {
  return SUtils.combineStyles(
    SUtils.createBackgroundStyle(customisation.backgroundColor),
    SUtils.createBorderStyle(customisation.borderColor)
  );
});

const iconStyle = computed(() => {
  return SUtils.createTextStyle(customisation.iconColor);
});

const textStyle = computed(() => {
  return SUtils.createTextStyle(customisation.textColor);
});

const closeIconStyle = computed(() => {
  return SUtils.createTextStyle(customisation.endCallIconColor);
});

const handleEndCall = () => {
  emit('end-call');
};

const handleToggleMute = () => {
  emit('toggle-mute');
};
</script>

<template>
  <div class="shadow-hard voice-call-indicator-wrapper" data-testid="voice-call-indicator">
    <div class="input-radius voice-call-indicator" :style="containerStyle">
      <div class="voice-call-indicator__icon-wrapper" :style="iconStyle">
        <div class="pulse-ring pulse-ring--1"></div>
        <div class="pulse-ring pulse-ring--2"></div>
        <div class="pulse-ring pulse-ring--3"></div>
        <div class="voice-call-indicator__icon">
          <v-icon name="MdAccountcircleRound" />
        </div>
      </div>
      <div class="voice-call-indicator__content">
        <div class="theme-font-subtext voice-call-indicator__text" :style="textStyle">{{ text }}</div>
        <div class="theme-font-main voice-call-indicator__timer" :style="textStyle" data-testid="voice-call-duration">
          {{ props.callDuration }}
        </div>
      </div>
      <div class="voice-call-indicator__actions">
        <button
          v-if="showMuteButton"
          type="button"
          class="voice-call-indicator__action"
          :style="closeIconStyle"
          :disabled="disabled"
          aria-label="Toggle mute"
          data-testid="voice-call-mute-button"
          @click="handleToggleMute"
        >
          <MicOffIcon v-if="isMuted" class="voice-call-indicator__action-icon" data-testid="voice-call-mic-off-icon" />
          <MicIcon v-else class="voice-call-indicator__action-icon" data-testid="voice-call-mic-on-icon" />
        </button>
        <button
          type="button"
          class="voice-call-indicator__action"
          :style="closeIconStyle"
          :disabled="disabled"
          aria-label="End voice call"
          data-testid="voice-call-end-button"
          @click="handleEndCall"
        >
          <EndCallIcon class="voice-call-indicator__action-icon" />
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.voice-call-indicator-wrapper {
  padding: 12px;
}

.voice-call-indicator {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  position: sticky;
  top: 0;
  z-index: 10;
}

.voice-call-indicator__icon-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 40px;
  height: 40px;
}

.pulse-ring {
  position: absolute;
  border-radius: 50%;
  border: 2px solid currentColor;
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

.pulse-ring--1 {
  width: 100%;
  height: 100%;
  opacity: 0.6;
  animation-delay: 0s;
}

.pulse-ring--2 {
  width: 120%;
  height: 120%;
  opacity: 0.4;
  animation-delay: 0.4s;
}

.pulse-ring--3 {
  width: 140%;
  height: 140%;
  opacity: 0.2;
  animation-delay: 0.8s;
}

@keyframes pulse {
  0% {
    transform: scale(0.8);
    opacity: 0.6;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.3;
  }
  100% {
    transform: scale(0.8);
    opacity: 0.6;
  }
}

.voice-call-indicator__icon {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.voice-call-indicator__content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.voice-call-indicator__timer {
  font-size: 12px;
  opacity: 0.8;
}

.voice-call-indicator__actions {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
}

.voice-call-indicator__action {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 4px;
  background: none;
  border: none;
  outline: none;
  box-shadow: none;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover:not(:disabled) {
    opacity: 0.7;
  }

  &:focus,
  &:active {
    outline: none;
    border: none;
    box-shadow: none;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }
}

.voice-call-indicator__action-icon {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
}
</style>
